/**
 * Skafe Brazil Storefront Helpers
 *
 * Provides:
 * 1. Real-time ViaCEP address autofill for customer address forms (Street, Neighborhood, City, State).
 * 2. Real-time CPF / CNPJ dynamic masking (000.000.000-00 or 00.000.000/0000-00).
 * 3. Mathematical Modulo-11 check-digit validation for CPF and CNPJ.
 */

(function () {
  'use strict';

  // --- Modulo 11 Validation Algorithms ---

  function validateCpf(value) {
    const digits = (value || '').replace(/\D/g, '');
    if (digits.length !== 11) return false;
    if (/^(\d)\1{10}$/.test(digits)) return false;

    let sum = 0, rest;
    for (let i = 1; i <= 9; i++) {
      sum += parseInt(digits.substring(i - 1, i), 10) * (11 - i);
    }
    rest = (sum * 10) % 11;
    if (rest === 10 || rest === 11) rest = 0;
    if (rest !== parseInt(digits.substring(9, 10), 10)) return false;

    sum = 0;
    for (let i = 1; i <= 10; i++) {
      sum += parseInt(digits.substring(i - 1, i), 10) * (12 - i);
    }
    rest = (sum * 10) % 11;
    if (rest === 10 || rest === 11) rest = 0;
    return rest === parseInt(digits.substring(10, 11), 10);
  }

  function validateCnpj(value) {
    const digits = (value || '').replace(/\D/g, '');
    if (digits.length !== 14) return false;
    if (/^(\d)\1{13}$/.test(digits)) return false;

    let size = digits.length - 2;
    let numbers = digits.substring(0, size);
    const checkDigits = digits.substring(size);
    let sum = 0;
    let pos = size - 7;

    for (let i = size; i >= 1; i--) {
      sum += parseInt(numbers.charAt(size - i), 10) * pos--;
      if (pos < 2) pos = 9;
    }
    let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (result !== parseInt(checkDigits.charAt(0), 10)) return false;

    size = size + 1;
    numbers = digits.substring(0, size);
    sum = 0;
    pos = size - 7;
    for (let i = size; i >= 1; i--) {
      sum += parseInt(numbers.charAt(size - i), 10) * pos--;
      if (pos < 2) pos = 9;
    }
    result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    return result === parseInt(checkDigits.charAt(1), 10);
  }

  function validateCpfCnpj(value) {
    const digits = (value || '').replace(/\D/g, '');
    if (digits.length === 11) return validateCpf(digits);
    if (digits.length === 14) return validateCnpj(digits);
    return false;
  }

  // --- Masking Formats ---

  function maskCpfCnpj(value) {
    const digits = (value || '').replace(/\D/g, '').slice(0, 14);
    if (digits.length <= 11) {
      // CPF: 000.000.000-00
      let v = digits;
      if (v.length > 9) v = `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6, 9)}-${v.slice(9)}`;
      else if (v.length > 6) v = `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6)}`;
      else if (v.length > 3) v = `${v.slice(0, 3)}.${v.slice(3)}`;
      return v;
    }
    // CNPJ: 00.000.000/0000-00
    let v = digits;
    if (v.length > 12) v = `${v.slice(0, 2)}.${v.slice(2, 5)}.${v.slice(5, 8)}/${v.slice(8, 12)}-${v.slice(12)}`;
    else if (v.length > 8) v = `${v.slice(0, 2)}.${v.slice(2, 5)}.${v.slice(5, 8)}/${v.slice(8)}`;
    else if (v.length > 5) v = `${v.slice(0, 2)}.${v.slice(2, 5)}.${v.slice(5)}`;
    else if (v.length > 2) v = `${v.slice(0, 2)}.${v.slice(2)}`;
    return v;
  }

  function maskCep(value) {
    const digits = (value || '').replace(/\D/g, '').slice(0, 8);
    if (digits.length > 5) {
      return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    }
    return digits;
  }

  // --- ViaCEP Autofill for Address Forms ---

  async function fillAddressFromCep(zipInput) {
    const cleanCep = (zipInput.value || '').replace(/\D/g, '');
    if (cleanCep.length !== 8) return;

    const form = zipInput.closest('form') || document;
    const streetInput = form.querySelector('input[name*="[address1]"], input[id*="AddressAddress1"]');
    const neighborhoodInput = form.querySelector('input[name*="[address2]"], input[id*="AddressAddress2"]');
    const cityInput = form.querySelector('input[name*="[city]"], input[id*="AddressCity"]');
    const provinceSelect = form.querySelector('select[name*="[province]"], select[id*="AddressProvince"]');

    try {
      zipInput.style.opacity = '0.7';
      const res = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`, {
        signal: AbortSignal.timeout(4000)
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.erro) return;

      if (streetInput && data.logradouro && !streetInput.value) {
        streetInput.value = data.logradouro;
        streetInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (neighborhoodInput && data.bairro && !neighborhoodInput.value) {
        neighborhoodInput.value = data.bairro;
        neighborhoodInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (cityInput && data.localidade) {
        cityInput.value = data.localidade;
        cityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (provinceSelect && data.uf) {
        // Find matching option by text or value
        const targetUf = data.uf.toUpperCase();
        for (let i = 0; i < provinceSelect.options.length; i++) {
          const opt = provinceSelect.options[i];
          if (opt.value.toUpperCase() === targetUf || opt.text.toUpperCase().includes(targetUf)) {
            provinceSelect.selectedIndex = i;
            provinceSelect.dispatchEvent(new Event('change', { bubbles: true }));
            break;
          }
        }
      }
    } catch (e) {
      console.warn('ViaCEP lookup warning:', e);
    } finally {
      zipInput.style.opacity = '1';
    }
  }

  // --- Attach Handlers ---

  function initBrazilHelpers() {
    // 1. Attach ViaCEP autofill to ZIP inputs
    const zipInputs = document.querySelectorAll('input[name*="[zip]"], input[id*="AddressZip"], input[data-mask="cep"]');
    zipInputs.forEach((input) => {
      if (input.dataset.skafeCepInit) return;
      input.dataset.skafeCepInit = 'true';

      input.addEventListener('input', (e) => {
        const masked = maskCep(e.target.value);
        e.target.value = masked;
        const clean = masked.replace(/\D/g, '');
        if (clean.length === 8) {
          fillAddressFromCep(input);
        }
      });

      input.addEventListener('blur', () => {
        fillAddressFromCep(input);
      });
    });

    // 2. Attach CPF / CNPJ masking and validation
    const taxInputs = document.querySelectorAll('input[name*="cpf"], input[name*="cnpj"], input[id*="cpf"], input[id*="cnpj"], input[data-mask="cpf"], input[data-mask="cnpj"], input[data-mask="cpf-cnpj"]');
    taxInputs.forEach((input) => {
      if (input.dataset.skafeTaxInit) return;
      input.dataset.skafeTaxInit = 'true';

      // Validation feedback element
      let feedback = input.parentElement.querySelector('.skafe-tax-feedback');
      if (!feedback) {
        feedback = document.createElement('div');
        feedback.className = 'skafe-tax-feedback caption';
        feedback.style.display = 'none';
        feedback.style.marginTop = '0.4rem';
        feedback.style.fontSize = '1.2rem';
        input.insertAdjacentElement('afterend', feedback);
      }

      function checkValidation(showEmptyWarning = false) {
        const val = input.value.trim();
        const digits = val.replace(/\D/g, '');

        if (!digits) {
          if (feedback) feedback.style.display = 'none';
          input.setCustomValidity('');
          return true;
        }

        if (digits.length !== 11 && digits.length !== 14) {
          if (showEmptyWarning && feedback) {
            feedback.innerHTML = '<span style="color: #b91c1c;">⚠️ Digite um CPF ou CNPJ completo.</span>';
            feedback.style.display = 'block';
            input.setCustomValidity('CPF ou CNPJ incompleto.');
          }
          return false;
        }

        const isValid = validateCpfCnpj(digits);
        if (isValid) {
          if (feedback) {
            feedback.innerHTML = '<span style="color: #00573f;">✓ ' + (digits.length === 11 ? 'CPF válido' : 'CNPJ válido') + '</span>';
            feedback.style.display = 'block';
          }
          input.setCustomValidity('');
          return true;
        } else {
          if (feedback) {
            feedback.innerHTML = '<span style="color: #b91c1c;">⚠️ ' + (digits.length === 11 ? 'CPF inválido.' : 'CNPJ inválido.') + ' Verifique os números.</span>';
            feedback.style.display = 'block';
          }
          input.setCustomValidity('CPF ou CNPJ inválido.');
          return false;
        }
      }

      input.addEventListener('input', (e) => {
        const masked = maskCpfCnpj(e.target.value);
        e.target.value = masked;
        const digits = masked.replace(/\D/g, '');
        if (digits.length === 11 || digits.length === 14) {
          checkValidation(false);
        } else {
          if (feedback) feedback.style.display = 'none';
        }
      });

      input.addEventListener('blur', () => {
        checkValidation(true);
      });
    });
  }

  // Run on DOM load and on dynamic Shopify section renders
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBrazilHelpers);
  } else {
    initBrazilHelpers();
  }

  document.addEventListener('shopify:section:load', initBrazilHelpers);

  // Expose global helper API
  window.SkafeBrazil = {
    validateCpf,
    validateCnpj,
    validateCpfCnpj,
    maskCpfCnpj,
    maskCep,
    init: initBrazilHelpers
  };
})();
